import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Camera, Upload, Search, ShoppingBag, RotateCw, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { AnalyzerProgress, AnalyzerErrorCard } from "@/components/AnalyzerFlow";

// FindSimilarScreen — upload a clothing photo, AI identifies items, we surface
// real searchable shop links for each piece. No prices, no sizes, no maps —
// just identify and link out to where users can actually buy similar items.

// Build category-specific real-shop search URLs. Each link is a live search
// at that retailer for the AI's per-item query. Click → product page.
function buildShopLinks(category, query) {
  const cat = (category || "other").toLowerCase();
  const q = encodeURIComponent((query || "").trim() || "outfit");

  // Category → ordered list of {name, url, accent?}. Always lead with the
  // most relevant retailer per category, then add Google Shopping as a
  // universal fallback so users still get visual results.
  const G  = { name: "Google Shopping", url: `https://www.google.com/search?tbm=shop&q=${q}` };
  const AZ = { name: "Amazon",          url: `https://www.amazon.com/s?k=${q}` };
  const SH = { name: "SHEIN",           url: `https://us.shein.com/pdsearch/${q}/` };

  const presets = {
    top: [
      { name: "Zara",     url: `https://www.zara.com/us/en/search?searchTerm=${q}` },
      { name: "H&M",      url: `https://www2.hm.com/en_us/search-results.html?q=${q}` },
      SH,
      { name: "Uniqlo",   url: `https://www.uniqlo.com/us/en/search?q=${q}` },
      { name: "ASOS",     url: `https://www.asos.com/us/search/?q=${q}` },
      G, AZ,
    ],
    bottom: [
      { name: "Levi's",   url: `https://www.levi.com/US/en_US/search?q=${q}` },
      { name: "Zara",     url: `https://www.zara.com/us/en/search?searchTerm=${q}` },
      { name: "H&M",      url: `https://www2.hm.com/en_us/search-results.html?q=${q}` },
      SH,
      { name: "Uniqlo",   url: `https://www.uniqlo.com/us/en/search?q=${q}` },
      { name: "ASOS",     url: `https://www.asos.com/us/search/?q=${q}` },
      G, AZ,
    ],
    outerwear: [
      { name: "Zara",        url: `https://www.zara.com/us/en/search?searchTerm=${q}` },
      { name: "H&M",         url: `https://www2.hm.com/en_us/search-results.html?q=${q}` },
      SH,
      { name: "Uniqlo",      url: `https://www.uniqlo.com/us/en/search?q=${q}` },
      { name: "Nordstrom",   url: `https://www.nordstrom.com/sr?keyword=${q}` },
      { name: "ASOS",        url: `https://www.asos.com/us/search/?q=${q}` },
      G,
    ],
    shoes: [
      { name: "Nike",        url: `https://www.nike.com/w?q=${q}` },
      { name: "Adidas",      url: `https://www.adidas.com/us/search?q=${q}` },
      { name: "StockX",      url: `https://stockx.com/search?s=${q}` },
      { name: "GOAT",        url: `https://www.goat.com/search?query=${q}` },
      { name: "Foot Locker", url: `https://www.footlocker.com/search?query=${q}` },
      G, AZ,
    ],
    bag: [
      { name: "Coach",     url: `https://www.coach.com/search?q=${q}` },
      { name: "Nordstrom", url: `https://www.nordstrom.com/sr?keyword=${q}` },
      { name: "Zara",      url: `https://www.zara.com/us/en/search?searchTerm=${q}` },
      { name: "ASOS",      url: `https://www.asos.com/us/search/?q=${q}` },
      G, AZ,
    ],
    hat: [
      { name: "New Era",   url: `https://www.neweracap.com/search?q=${q}` },
      { name: "Lids",      url: `https://www.lids.com/search?query=${q}` },
      { name: "ASOS",      url: `https://www.asos.com/us/search/?q=${q}` },
      G, AZ,
    ],
    watch: [
      { name: "Nordstrom",     url: `https://www.nordstrom.com/sr?keyword=${q}` },
      { name: "Macy's",        url: `https://www.macys.com/shop/search?keyword=${q}` },
      { name: "Watch Station", url: `https://www.watchstation.com/us/search?q=${q}` },
      G, AZ,
    ],
    jewelry: [
      { name: "Mejuri",     url: `https://mejuri.com/search?q=${q}` },
      { name: "Pandora",    url: `https://us.pandora.net/en/search/?q=${q}` },
      { name: "Nordstrom",  url: `https://www.nordstrom.com/sr?keyword=${q}` },
      G, AZ,
    ],
    dress: [
      { name: "Zara",        url: `https://www.zara.com/us/en/search?searchTerm=${q}` },
      { name: "ASOS",        url: `https://www.asos.com/us/search/?q=${q}` },
      { name: "H&M",         url: `https://www2.hm.com/en_us/search-results.html?q=${q}` },
      SH,
      { name: "Reformation", url: `https://www.thereformation.com/search?q=${q}` },
      { name: "Revolve",     url: `https://www.revolve.com/r/Search.jsp?search=${q}` },
      G, AZ,
    ],
    accessory: [
      { name: "ASOS",      url: `https://www.asos.com/us/search/?q=${q}` },
      { name: "Nordstrom", url: `https://www.nordstrom.com/sr?keyword=${q}` },
      { name: "Zara",      url: `https://www.zara.com/us/en/search?searchTerm=${q}` },
      SH,
      G, AZ,
    ],
    other: [G, AZ, SH, { name: "ASOS", url: `https://www.asos.com/us/search/?q=${q}` }],
  };

  return presets[cat] || presets.other;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error("read failed"));
    r.onload  = () => resolve((r.result || "").toString().split(",").pop());
    r.readAsDataURL(file);
  });
}

export default function FindSimilarScreen() {
  const navigate = useNavigate();
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const [previewUrl, setPreviewUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleFile = async (file) => {
    if (!file) return;
    setError(null);
    setResult(null);
    setBusy(true);
    setPreviewUrl(URL.createObjectURL(file));
    try {
      const image_base64 = await fileToBase64(file);

      // Reliability per master spec #8: retry up to 2 extra times client-side
      // on top of the backend's own retries. Total max attempts = 3 here,
      // each of which the backend ALSO retries 3x, so the user effectively
      // gets 9 shots before we surface a soft error.
      let lastErr = null;
      let data = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const res = await api.post("/outfit/find-similar", {
            image_base64,
            image_mime: file.type || "image/jpeg",
          });
          data = res.data;
          break;
        } catch (err) {
          lastErr = err;
          // Don't retry on 4xx (bad input, auth, quota) — user-actionable.
          const status = err?.response?.status;
          if (status && status >= 400 && status < 500) break;
          // Brief backoff before next try
          await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
        }
      }
      if (!data) throw lastErr || new Error("retry_failed");

      if (data.error === "no_clothing_detected") {
        setError({ message: "We couldn't spot any clothing in that photo. Try one where the garments are clearly visible." });
        setResult(null);
      } else if (data.error === "temporary") {
        // Soft retry — server tried 3x and still failed. Friendly tone.
        setError({ message: data.message || "We're having trouble finding similar items right now. Please try again in a moment, or upload another photo." });
        setResult(null);
      } else {
        setResult(data);
      }
    } catch (err) {
      // Anything that escapes the retry loop. Surface only the friendly message.
      const status = err?.response?.status;
      let message;
      if (status === 402 || status === 429) {
        message = "You've hit today's free analysis limit. Try again tomorrow or upgrade for unlimited.";
      } else if (status === 401) {
        message = "Please sign in again to continue.";
      } else if (status === 400) {
        message = err?.response?.data?.detail || "That photo didn't work. Try another shot.";
      } else {
        message = "We're having trouble finding similar items right now. Please try again in a moment, or upload another photo.";
      }
      setError({ message });
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setResult(null);
    setError(null);
    setPreviewUrl(null);
  };

  return (
    <div className="screen with-nav fade-in" data-testid="find-similar-screen">
      <div className="top-bar">
        <button className="icon-btn" onClick={() => navigate("/style")} aria-label="Back" data-testid="find-back-btn">
          <ChevronLeft size={18} strokeWidth={1.7} />
        </button>
        <div className="logo-mark">FIND SIMILAR</div>
        <div style={{ width: 36 }} />
      </div>

      <div className="eyebrow" style={{ marginBottom: 8 }}>UPLOAD · IDENTIFY · BUY</div>
      <h2 className="h1" style={{ fontSize: 28, marginBottom: 8, lineHeight: 1.15 }}>
        Find&nbsp;<em className="serif" style={{ fontStyle: "italic", fontWeight: 300 }}>any piece</em><br />you love.
      </h2>
      <p className="body" style={{ color: "var(--text-dim)", marginTop: 0 }}>
        Upload a clothing photo — your own, a screenshot, anyone&apos;s outfit. We identify the pieces and point you to where to buy them.
      </p>

      {/* Upload OR show preview */}
      {!result && !busy && !error && (
        <div style={{ marginTop: 18 }}>
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: "none" }}
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="find-camera-input"
          />
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="find-gallery-input"
          />
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <button type="button" className="btn btn-gold" style={{ justifyContent: "center" }} onClick={() => cameraInputRef.current?.click()} data-testid="find-camera-btn">
              <Camera size={16} /> Take a photo
            </button>
            <button type="button" className="btn btn-outline" style={{ justifyContent: "center" }} onClick={() => galleryInputRef.current?.click()} data-testid="find-gallery-btn">
              <Upload size={16} /> Upload from gallery
            </button>
          </div>
        </div>
      )}

      {busy && (
        <div style={{ marginTop: 22 }}>
          <AnalyzerProgress message="Identifying clothing…" subMessage="Scanning the photo for individual pieces" testId="find-similar-analyzing" />
          {previewUrl && (
            <div style={{ marginTop: 16, borderRadius: 16, overflow: "hidden", border: "1px solid var(--line)" }}>
              <img src={previewUrl} alt="" style={{ width: "100%", display: "block" }} />
            </div>
          )}
        </div>
      )}

      {error && (
        <div style={{ marginTop: 18 }}>
          <AnalyzerErrorCard
            error={error}
            onRetry={reset}
            testId="find-similar-error"
          />
        </div>
      )}

      {result && (
        <div data-testid="find-similar-result">
          {previewUrl && (
            <div className="outfit-section" style={{ borderRadius: 16, overflow: "hidden", border: "1px solid var(--line)", padding: 0 }}>
              <img src={previewUrl} alt="" style={{ width: "100%", display: "block" }} />
            </div>
          )}

          {result.overall_vibe && (
            <div className="outfit-section" data-testid="find-similar-vibe">
              <div className="eyebrow" style={{ marginBottom: 6 }}>OVERALL VIBE</div>
              <div className="serif" style={{ fontFamily: "'Fraunces', serif", fontSize: 22, color: "var(--text)" }}>
                {result.overall_vibe}
              </div>
            </div>
          )}

          <div className="outfit-section" data-testid="find-similar-items">
            <div className="eyebrow" style={{ marginBottom: 12 }}>
              {result.items.length} ITEM{result.items.length === 1 ? "" : "S"} IDENTIFIED
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {result.items.map((it, i) => {
                const cat = (it.category || "other").toLowerCase();
                const links = buildShopLinks(cat, it.search_query || it.label);
                return (
                  <div key={i} className="card find-item-card" data-testid={`find-item-${i}`}>
                    <div className="find-item-head">
                      <div>
                        <div className="find-item-cat">{it.category}</div>
                        <div className="find-item-label">{it.label}</div>
                        {it.colors?.length > 0 && (
                          <div className="find-item-meta">
                            {(it.colors || []).map((c, j) => (
                              <span key={j} className="chip find-item-color" style={{ pointerEvents: "none" }}>{c}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Real clickable shop links — each opens a live search at the retailer. */}
                    <div className="find-item-links" data-testid={`find-item-stores-${i}`}>
                      <div className="find-item-links-label">
                        <ShoppingBag size={11} strokeWidth={2} /> Shop similar at
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
                        {links.map((s) => (
                          <a
                            key={s.name}
                            href={s.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="card"
                            style={{
                              padding: "10px 12px",
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              textDecoration: "none",
                              background: "var(--bg-elev)",
                              border: "1px solid var(--line)",
                            }}
                            data-testid={`find-item-store-${i}-${s.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          >
                            <span style={{ flex: 1, color: "var(--text)", fontSize: 13 }}>{s.name}</span>
                            <span className="tiny" style={{ color: "var(--gold)", textTransform: "uppercase", letterSpacing: "0.10em", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}>
                              Shop <ExternalLink size={11} strokeWidth={2} />
                            </span>
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <button type="button" className="btn btn-outline" style={{ marginTop: 14, width: "100%", justifyContent: "center" }} onClick={reset} data-testid="find-similar-new-btn">
            <RotateCw size={14} /> Find another piece
          </button>
        </div>
      )}
    </div>
  );
}
